import debounce from 'just-debounce';

/**
 * @class HeaderToolBar
 * @classdesc The cart drawer component is used to handle the cart drawer functionality.
 *
 * @extends Core.Component
 */
export default class HeaderToolBar extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.slider = el.querySelector('[data-announcement-slider]');
    this.headerToolbar = el.querySelector('[data-header-toolbar-list]');
    this.slides = el.querySelectorAll('[data-announcement-block]');
    this.controls = el.querySelector('[data-header-toolbar-controls]');
    this.controlButtons = el.querySelectorAll('[data-header-toolbar-control-button]');
    this.previousButton = el.querySelector('[data-header-toolbar-direction="previous"]');
    this.nextButton = el.querySelector('[data-header-toolbar-direction="next"]');
    this.currentIndex = 1;
    this.min = 1;
    this.max = this.slides.length;

    if (this.data.settings.block_size > 1) {
      this._setButtonStates();
    }

    this.events.register(this.controls, 'click', debounce(e => {
      this._setIndex(e.target.dataset.headerToolbarDirection);

      const link = e.target.closest('a');
      const matchingText = this.headerToolbar.querySelector(`li[id='${link.getAttribute('href').replace('#', '')}-${this.currentIndex}']`);

      e.preventDefault();
      this.headerToolbar.scrollTo({ left: matchingText.offsetLeft, behavior: 'smooth' });

      this._setActiveState(matchingText);

      this._setTabIndex();
    }), 500);

    this.events.register(this.headerToolbar, 'scroll', debounce(() => this._setActiveSlide(), 100));
    this.events.register(el, 'announcement-block:select', (e) => this.onBlockSelect(e));
  }

  /**
   * Fires when an announcement block is selected in the theme editor
   */
  onBlockSelect(e) {
    this.forceIndex(e);
  }

  /**
   * Force a specific announcement block to slide into view
   *
   */
  forceIndex(e) {
    this.currentIndex = Number(e.detail.index);
    this.headerToolbar.scrollTo({ left: e.target.offsetLeft, behavior: 'smooth' });
    this._setButtonStates();

    this._setActiveState(e.target);

    this._setTabIndex();
  }

  /**
   * Controls which slide is active
   *
   * @private
   */
  _setActiveSlide() {
    const currentSlideIndex = Math.floor(this.headerToolbar.scrollLeft / this.headerToolbar.offsetWidth);

    this.controlButtons.forEach((button, index) => {
      if (index === currentSlideIndex) {
        button.dataset.active = 'true';
      } else {
        button.dataset.active = 'false';
      }
    });
  }

  /**
   * Loops through slides and sets the active state
   *
   * @param {HTMLElement} target - The target element
   * @private
   */
  _setActiveState(el) {
    this.slides.forEach(slide => {
      slide.dataset.active = 'false';
    });

    el.dataset.active = 'true';
  }

  /**
   * Sets the current slide index
   *
   * @param {string} direction - The direction of the slide
   * @private
   */
  _setIndex(direction) {
    if (direction === 'next' && this.nextButton.dataset.disabled !== 'true') {
      if (this.currentIndex < this.max) {
        this.currentIndex++;
      }
    } else if (direction === 'previous' && this.previousButton.dataset.disabled !== 'true') {
      if (this.currentIndex > this.min) {
        this.currentIndex--;
      }
    }

    this._setButtonStates();
  }

  /**
   * Handles state of the control buttons
   *
   * @private
   */
  _setButtonStates() {
    if (this.slides.length < 2) return;
    if (this.currentIndex === this.max) {
      this.nextButton.dataset.disabled = 'true';
    } else {
      this.nextButton.dataset.disabled = 'false';
    }

    if (this.currentIndex === this.min) {
      this.previousButton.dataset.disabled = 'true';
    } else {
      this.previousButton.dataset.disabled = 'false';
    }
  }

  /**
   * Controls which links are tabbable in the current context of the slider
   *
   * @private
   */
  _setTabIndex() {
    this.slides.forEach(slide => {
      const aTags = slide.querySelectorAll('a');

      if (slide.dataset.active === 'true') {
        aTags.forEach(a => {
          a.setAttribute('tabindex', '0');
        });
      } else {
        aTags.forEach(a => {
          a.setAttribute('tabindex', '-1');
        });
      }
    });
  }

  /**
   * Returns the height of the header toolbar.
   *
   */
  getHeight() {
    return this.el.offsetHeight;
  }

  /**
   * Unregisters all event listeners
   *
   */
  unload() {
    this.events.unregisterAll();
  }
}
